import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/articles';

interface FooterProps {
  formUrl: string;
}

export default function Footer({ formUrl }: FooterProps) {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeMsg, setSubscribeMsg] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubscribeStatus('loading');

    try {
      const formData = new FormData(e.currentTarget);
      const honeypot = formData.get('website_alt') as string;
      if (honeypot && honeypot.trim()) {
        setSubscribeStatus('success');
        setSubscribeMsg('感谢订阅！请查收您的邮箱确认邮件。');
        setEmail('');
        return;
      }
      formData.delete('website_alt');

      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMsg('感谢订阅！请查收您的邮箱确认邮件。');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '订阅失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setSubscribeStatus('success');
          setSubscribeMsg('感谢订阅！');
          setEmail('');
        } else {
          setSubscribeStatus('error');
          setSubscribeMsg(serverMsg);
        }
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMsg('网络错误，请稍后重试');
    }
  };

  return (
    <footer className="bg-background-900 text-background-100">
      <div className="w-full px-4 md:px-6 py-14">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            <div>
              <h4 className="text-lg font-heading font-bold text-background-50 mb-4">
                <Link to="/" className="hover:text-primary-300 transition-colors cursor-pointer">亚洲在线</Link>
              </h4>
              <p className="text-sm text-background-300 leading-relaxed">
                连接亚洲，精彩在线。汇聚亚洲顶级数字内容、娱乐资讯与在线服务的一站式平台。
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 mb-4">内容分类</h4>
              <ul className="flex flex-col gap-2">
                {categories.slice(0, 4).map((cat) => (
                  <li key={cat.id}>
                    <Link
                      to={`/category/${cat.id}`}
                      className="text-sm text-background-300 hover:text-primary-300 transition-colors cursor-pointer"
                    >
                      {cat.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 mb-4">快速链接</h4>
              <ul className="flex flex-col gap-2">
                <li>
                  <Link to="/about" className="text-sm text-background-300 hover:text-primary-300 transition-colors cursor-pointer">
                    关于我们
                  </Link>
                </li>
                <li>
                  <Link to="/category/tech" className="text-sm text-background-300 hover:text-primary-300 transition-colors cursor-pointer">
                    科技前沿
                  </Link>
                </li>
                <li>
                  <Link to="/category/lifestyle" className="text-sm text-background-300 hover:text-primary-300 transition-colors cursor-pointer">
                    数字生活
                  </Link>
                </li>
                <li>
                  <Link to="/category/travel" className="text-sm text-background-300 hover:text-primary-300 transition-colors cursor-pointer">
                    旅游出行
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 mb-4">订阅资讯</h4>
              <p className="text-sm text-background-300 mb-4">获取最新的亚洲数字生活资讯</p>
              <form onSubmit={handleSubscribe} data-readdy-form="">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="输入您的邮箱"
                    required
                    className="flex-1 px-4 py-2.5 text-sm bg-background-800 border border-background-700 rounded-lg text-background-100 placeholder:text-background-500 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={subscribeStatus === 'loading'}
                    className="px-5 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {subscribeStatus === 'loading' ? '提交中...' : '订阅'}
                  </button>
                </div>
                <div className="website-alt-field">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                {subscribeMsg && (
                  <p className={`text-xs mt-2 ${subscribeStatus === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                    {subscribeMsg}
                  </p>
                )}
              </form>
            </div>
          </div>

          <div className="border-t border-background-700 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-background-400">
              &copy; 2026 亚洲在线. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-background-400 hover:text-background-200 transition-colors cursor-pointer" aria-label="微信">
                <i className="ri-wechat-line text-lg"></i>
              </a>
              <a href="#" className="text-background-400 hover:text-background-200 transition-colors cursor-pointer" aria-label="微博">
                <i className="ri-weibo-line text-lg"></i>
              </a>
              <a href="#" className="text-background-400 hover:text-background-200 transition-colors cursor-pointer" aria-label="邮箱">
                <i className="ri-mail-line text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}